/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect, useMemo, useState } from "react";

// react-router components
import { Navigate, Route, Routes, useLocation } from "react-router-dom";

// @mui material components
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import "./App.css";
// Material Dashboard 2 React example components
import Sidenav from "examples/Sidenav";
import Configurator from "examples/Configurator";

// Material Dashboard 2 React themes
import theme from "assets/theme";
import themeRTL from "assets/theme/theme-rtl";

// Material Dashboard 2 React Dark Mode themes
import themeDark from "assets/theme-dark";

// RTL plugins
import rtlPlugin from "stylis-plugin-rtl";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";

// Material Dashboard 2 React routes
import routes from "routes";

// Material Dashboard 2 React contexts
import { setMiniSidenav, setOpenConfigurator, useMaterialUIController } from "context";

// Images
import AppLayout from "./layouts/masterLayout";
import ReportListPage from "./layouts/report/ReportListPage";
import ReportListImage from "./layouts/report/ReportListImage";
import MapPage from "./layouts/Map/MapPage";
import ReportTotalListPage from "./layouts/report/ReportTotalListPage";
import CreateJensComponent from "./components/Definition/Jens/JensCreateComponent";
import DefinitionPage from "./layouts/definaition/definition_page";
import OriginScreen from "./layouts/definaition/origin/OriginScreen";
import CreateOriginComponent from "./components/Definition/Origin/OriginCreateComponent";
import DestinationScreen from "./layouts/definaition/destination/DestinationScreen";
import CreateDestinationComponent from "./components/Definition/Destination/DestinationCreateComponent";
import MachineGroupScreen from "./layouts/definaition/machine_group/MachineGroupScreen";
import CreateMachineGroupComponent from "./components/Definition/MachineGroup/MachineGroupCreateComponent";
import MachineTypeScreen from "./layouts/definaition/machine_types/MachineTypeScreen";
import CreateMachineTypesComponent from "./components/Definition/MachineTypes/MachineTypesCreateComponent";
import DriverScreen from "./layouts/definaition/drivers/DriverScreen";
import CreateDriverComponent from "./components/Definition/Driver/DriverCreateComponent";
import MachineScreen from "./layouts/definaition/machine/MachineScreen";
import CreateMachineComponent from "./components/Definition/Machine/MachineCreateComponent";
import JensScreen from "./layouts/definaition/jens/JensScreen";
import ReportListWeightBridgePage from "./layouts/report/WeightBridge/ReportListWeightBridgePage";
import CreateUserComponent from "./layouts/definaition/users/create_user";
import SuperAdminRoute from "./middleware/superadmin/SuperAdminRoute";
import JensCodeScreen from "./layouts/definaition/jens-codes/JensCodeScreen";
import CreateJensCodeComponent from "./components/Definition/JensCodes/JensCodesCreateComponent";
import OperatorsScreen from "./layouts/definaition/operators/OperatorsScreen";
import CreateOperatorComponent from "./components/Definition/Operator/OperatorCreateComponent";
import EquipmentScreen from "./layouts/definaition/equipment/EquipmentScreen";
import CreateEquipmentComponent from "./components/Definition/Equipment/EquipmentCreateComponent";
import Peymankar from "./layouts/definaition/peymankar/Peymankar";
import CreatPeymankarComponent from "./components/Definition/Peymankar/PeymankarCreateComponent";
import CreateProjectComponent from "./components/Definition/Project/ProjectCreateComponent";
import ProjectScreen from "./layouts/definaition/projects/ProjectScreen";
import LocationScreen from "./layouts/definaition/locations/LocationScreen";
import CreateLocationsComponent from "./components/Definition/Locations/LocationCreateComponent";
import WeightScreen from "./layouts/definaition/weight/WeightScreen";
import CreateWeightComponent from "./components/Definition/Weight/WeightCreateComponent";
import ReportLicenseListPage from "./layouts/report/CargoLicense/ReportListLicensePage";
import ShiftScreen from "./layouts/definaition/shift/ShiftScreen";
import CreateShiftComponent from "./components/Definition/Shift/ShiftCreateComponent";
import DelayScreen from "./layouts/definaition/delays/DelayScreen";
import CreateDelayComponent from "./components/Definition/Delays/DelaysCreateComponent";
import DefectsScreen from "./layouts/definaition/defects/DefectsScreen";
import CreateDefectComponent from "./components/Definition/Defects/DefectsCreateComponent";
import {HeroUIProvider} from "@heroui/system";
import ReportFunctionRegistrationListPage
  from "./layouts/report/FunctionRegistration/ReportListFunctionRegistrationPage";
import ReportStopRegistrationListPage from "./layouts/report/StopRegistration/ReportListStopRegistrationPage";
import EditCargoPage from "./layouts/Cargo/CargoEdit";
import EditStoreCargoPage from "./layouts/store-cargo/StoreCargoEdit";
import PurchasorScreen from "./layouts/definaition/purchasor/PurchasorScreen";
import CreatePurchasorComponent from "./components/Definition/Purchasor/PurchasorCreateComponent";

export default function App() {
  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    direction,
    layout,
    openConfigurator,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
  } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [rtlCache, setRtlCache] = useState(null);
  const { pathname } = useLocation();

  // Cache for the rtl
  useMemo(() => {
    const cacheRtl = createCache({
      key: "rtl",
      stylisPlugins: [rtlPlugin],
    });

    setRtlCache(cacheRtl);
  }, []);

  // Open sidenav when mouse enter on mini sidenav
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  // Close sidenav when mouse leave mini sidenav
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  // Change the openConfigurator state
  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.collapse) {
        return getRoutes(route.collapse);
      }

      if (route.route) {
        return <Route exact path={route.route} element={route.component} key={route.key} />;
      }

      return null;
    });

  const configsButton = (
    <MDBox
      display="flex"
      justifyContent="center"
      alignItems="center"
      width="3.25rem"
      height="3.25rem"
      bgColor="white"
      shadow="sm"
      borderRadius="50%"
      position="fixed"
      right="2rem"
      bottom="2rem"
      zIndex={99}
      color="dark"
      sx={{ cursor: "pointer" }}
      onClick={handleConfiguratorOpen}
    >
      <Icon fontSize="small" color="inherit">
        settings
      </Icon>
    </MDBox>
  );

  return direction === "rtl" ? (
    <AppLayout>
      <CacheProvider value={rtlCache}>
        <ThemeProvider theme={themeRTL}>
          <HeroUIProvider>
          <CssBaseline />
          {layout === "dashboard" && (
            <>
              <Sidenav
                color={sidenavColor}
                brand={"../images/ic_launcher.png"}
                brandName="مدیریت بارگیری و تخلیه بار"
                routes={routes}
                onMouseEnter={handleOnMouseEnter}
                onMouseLeave={handleOnMouseLeave}
              />
            </>
          )}
          {layout === "vr" && <Configurator />}
          <Routes>
            {getRoutes(routes)}
            <Route path="/reports/lists" element={<ReportListPage />} />
            <Route path="/reports/lists/images" element={<ReportListImage />} />
            <Route path="/reports/lists/map" element={<MapPage  />} />
            <Route path="/reports-function-registration/lists" element={<ReportFunctionRegistrationListPage  />} />
            <Route path="/reports-stop-registration/lists" element={<ReportStopRegistrationListPage  />} />
            <Route path="/reports-total/lists" element={<ReportTotalListPage  />} />
            <Route path="/reports-weight/lists" element={<ReportListWeightBridgePage  />} />
            <Route path="/reports/license/lists" element={<ReportLicenseListPage  />} />
            <Route path="/cargos/edit/:id" element={<EditCargoPage />} />
            <Route path="/license-cargos/edit/:id" element={<EditStoreCargoPage />} />
            <Route path="/definition" element={<DefinitionPage  />} />
            <Route path="/jens" element={<JensScreen  />} />
            <Route path="/jens/create" element={<CreateJensComponent  />} />
            <Route path="/origin" element={<OriginScreen  />} />
            <Route path="/origin/create" element={<CreateOriginComponent  />} />
            <Route path="/purchasor" element={<PurchasorScreen  />} />
            <Route path="/purchasor/create" element={<CreatePurchasorComponent  />} />

            <Route path="/operators" element={<OperatorsScreen  />} />
            <Route path="/operators/create" element={<CreateOperatorComponent  />} />
            <Route path="/equipments" element={<EquipmentScreen  />} />
            <Route path="/equipments/create" element={<CreateEquipmentComponent  />} />
            <Route path="/shifts" element={<ShiftScreen  />} />
            <Route path="/shifts/create" element={<CreateShiftComponent  />} />
            <Route path="/defects" element={<DefectsScreen  />} />
            <Route path="/defects/create" element={<CreateDefectComponent  />} />
            <Route path="/delays" element={<DelayScreen  />} />
            <Route path="/delays/create" element={<CreateDelayComponent  />} />
            <Route path="/peymankar" element={<Peymankar  />} />
            <Route path="/peymankar/create" element={<CreatPeymankarComponent  />} />
            <Route path="/weights" element={<WeightScreen  />} />
            <Route path="/weights/create" element={<CreateWeightComponent  />} />
            <Route path="/projects" element={<ProjectScreen  />} />
            <Route path="/projects/create" element={<CreateProjectComponent  />} />
            <Route path="/locations" element={<LocationScreen  />} />
            <Route path="/locations/create" element={<CreateLocationsComponent  />} />
            <Route path="/destination" element={<DestinationScreen  />} />
            <Route path="/destination/create" element={<CreateDestinationComponent  />} />
            <Route path="/machine-group" element={<MachineGroupScreen  />} />
            <Route path="/machine-group/create" element={<CreateMachineGroupComponent  />} />
            <Route path="/machine-types" element={<MachineTypeScreen  />} />
            <Route path="/machine-types/create" element={<CreateMachineTypesComponent  />} />
            <Route path="/drivers" element={<DriverScreen  />} />
            <Route path="/drivers/create" element={<CreateDriverComponent  />} />
            <Route path="/machines" element={<MachineScreen  />} />
            <Route path="/machine/create" element={<CreateMachineComponent  />} />
            <Route path="/jensCodes" element={<JensCodeScreen  />} />
            <Route path="/jensCodes/create" element={<CreateJensCodeComponent  />} />
            <Route path="/users/create" element={<SuperAdminRoute><CreateUserComponent  /></SuperAdminRoute>} />
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
          </HeroUIProvider>
        </ThemeProvider>
      </CacheProvider>
    </AppLayout>
  ) : (

    <ThemeProvider theme={darkMode ? themeDark : theme}>
      <HeroUIProvider>
      <CssBaseline />
      {layout === "dashboard" && (
        <>
          <Sidenav
            color={sidenavColor}
            brand={(transparentSidenav && !darkMode) || whiteSidenav ? brandDark : brandWhite}
            brandName="بارگو"
            routes={routes}
            onMouseEnter={handleOnMouseEnter}
            onMouseLeave={handleOnMouseLeave}
          />
        </>
      )}
      {layout === "vr" && <Configurator />}
      <Routes>
        {getRoutes(routes)}
        <Route path="/reports/lists" element={<h1>asasa</h1>} />
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
      </HeroUIProvider>
    </ThemeProvider>
  );
}
